import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';

import type {
  RealtimeConnection,
  TRealtimeFrameOf,
  TRealtimeFrameType,
  TRealtimeStatus,
} from '../realtime';

interface IRealtimeContext {
  connection: RealtimeConnection;
}

export const RealtimeContext = createContext<IRealtimeContext | undefined>(undefined);

export const useRealtimeContext = (): IRealtimeContext => {
  const context = useContext(RealtimeContext);

  if (context === undefined) {
    throw new Error('useRealtimeContext must be used within RealtimeProvider');
  }

  return context;
};

export const useRealtimeStatus = (): TRealtimeStatus => {
  const { connection } = useRealtimeContext();

  const subscribe = useCallback(
    (onChange: () => void) => connection.subscribeStatus(onChange),
    [connection],
  );

  return useSyncExternalStore(subscribe, () => connection.status());
};

// The handler is held in a ref on purpose: putting it in the deps resubscribes every render, and frames go missing in the gap.
export const useRealtimeFrame = <K extends TRealtimeFrameType>(
  type: K,
  handler: (frame: TRealtimeFrameOf<K>) => void,
): void => {
  const { connection } = useRealtimeContext();
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => connection.on(type, (frame) => latest.current(frame)), [connection, type]);
};

export const useRealtimeReconnect = (): (() => void) => {
  const { connection } = useRealtimeContext();
  return useCallback(() => connection.reconnect(), [connection]);
};
